import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Modal,
    ModalContent,
    ModalOverlay,
    ModalBody,
    ModalHeader,
    useStyleConfig,
    useDisclosure,
    Button,
    HStack,
    Stack,
    Text,
    Input,
} from '@chakra-ui/react'
import CloseIcon from '../../../../components/icons/action/CloseIcon'
import {ShareIcon} from '../../../../components/icons/action'
import {FormattedMessage} from 'react-intl'
import {CheckIcon} from '../../../../components/icons/others'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const ShareWishlistModal = ({wishListData, customer}) => {
    const styles = useStyleConfig('Wishlist')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [isCopied, setIsCopied] = useState(false)
    const {locale} = useMultiSite()
    const {customParams} = getConfig()
    const updateCustomerProductList = useShopperCustomersMutation('updateCustomerProductList')

    const handleOpen = async () => {
        if (wishListData?.id && customer?.customerId) {
            updateCustomerProductList.mutate(
                {
                    parameters: {
                        listId: wishListData.id,
                        customerId: customer.customerId,
                    },
                    body: {public: true},
                },
                {
                    onError: (error) => console.error('Error updating wishlist:', error),
                },
            )
        }
        onOpen()
    }

    // Early return if no wishlist data
    if (!wishListData?.id) {
        return null
    }
    // Reset copied state when modal closes
    const handleClose = () => {
        setIsCopied(false)
        onClose()
    }

    const shareUrl = `https://${customParams.currentHost}/${locale.alias}/wishlist/share?wishlistId=${wishListData.id}`

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl)
            setIsCopied(true)
        } catch (err) {
            console.error('Failed to copy text: ', err)
        }
    }

    return (
        <>
            <HStack {...styles.iconShareFlex}>
                <ShareIcon {...styles.shareIcon} />
                <Button
                    display={{base: 'none', lg: 'flex'}}
                    variant={'text-button'}
                    onClick={handleOpen}
                >
                    <FormattedMessage
                        defaultMessage="Share your Wishlist"
                        id="account_wishlist.title.share_wishlist"
                    />
                </Button>
            </HStack>
            <Modal isOpen={isOpen} onClose={handleClose}>
                <ModalOverlay {...styles.modalOverlay} />
                <ModalContent {...styles.modalShareContent}>
                    <ModalHeader {...styles.modalShareHeader}>
                        <HStack {...styles.modalShareHeadingFlex}>
                            <Text {...styles.modalShareHeading}>
                                <FormattedMessage
                                    defaultMessage="Share your Wishlist"
                                    id="account_wishlist.title.share_wishlist"
                                />
                            </Text>
                            <CloseIcon data-testid="close-modal-button" onClick={handleClose} />
                        </HStack>
                    </ModalHeader>
                    <ModalBody {...styles.modalShareBody}>
                        <Stack {...styles.modalShareBodyStack}>
                            <Text {...styles.modalShareDescription}>
                                <FormattedMessage
                                    defaultMessage="Copy the link below to share your wishlist."
                                    id="account_wishlist.share_wishlist.description"
                                />
                            </Text>
                            <HStack {...styles.modalShareBodyHStack}>
                                <Input {...styles.modalSharePath} value={shareUrl} isReadOnly />
                                <Button
                                    variant="primary"
                                    onClick={handleCopy}
                                    isDisabled={isCopied}
                                >
                                    <FormattedMessage
                                        defaultMessage="Copy"
                                        id="account_wishlist.share_wishlist.copy"
                                    />
                                </Button>
                            </HStack>
                            {isCopied && (
                                <HStack {...styles.modalShareBodyCopied}>
                                    <CheckIcon {...styles.modalCheckIcon} />
                                    <Text>
                                        <FormattedMessage
                                            defaultMessage="URL copied"
                                            id="account_wishlist.share_wishlist.copied"
                                        />
                                    </Text>
                                </HStack>
                            )}
                        </Stack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    )
}

ShareWishlistModal.propTypes = {
    wishListData: PropTypes.shape({
        id: PropTypes.string.isRequired,
    }),
    customer: PropTypes.shape({
        customerId: PropTypes.string.isRequired,
    }),
}

ShareWishlistModal.defaultProps = {
    wishListData: null,
    customer: null,
}

export default ShareWishlistModal
